import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { 
  Users, Building2, Star, ShieldCheck, Trash2, CheckCircle, 
  XCircle, ArrowLeft, Crown, RefreshCw 
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { StarRating } from "../components/StarRating";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = ({ user, setUser }) => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, servicesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/services`, { withCredentials: true }),
      ]);

      setStats(statsRes.data);
      setUsers(usersRes.data);
      setServices(servicesRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleApproveService = async (serviceId) => {
    try {
      await axios.put(
        `${API}/admin/services/${serviceId}/approve`,
        {},
        { withCredentials: true }
      );
      setServices(
        services.map((s) =>
          s.service_id === serviceId ? { ...s, is_approved: true } : s
        )
      );
      setStats({ ...stats, pending_services: stats.pending_services - 1 });
      toast.success("Service approved!");
    } catch (error) {
      toast.error("Failed to approve service");
    }
  };

  const handleFeatureService = async (serviceId, isFeatured) => {
    try {
      await axios.put(
        `${API}/admin/services/${serviceId}/feature`,
        { is_featured: isFeatured },
        { withCredentials: true }
      );
      setServices(
        services.map((s) =>
          s.service_id === serviceId ? { ...s, is_featured: isFeatured } : s
        )
      );
      toast.success(isFeatured ? "Service featured!" : "Feature removed");
    } catch (error) {
      toast.error("Failed to update feature status");
    }
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      await axios.put(
        `${API}/admin/users/${userId}/role`,
        { role: newRole },
        { withCredentials: true }
      );
      setUsers(
        users.map((u) => (u.user_id === userId ? { ...u, role: newRole } : u))
      );
      toast.success("User role updated!");
    } catch (error) {
      toast.error("Failed to update role");
    }
  };

  const handleDeleteService = async (serviceId) => {
    try {
      await axios.delete(`${API}/services/${serviceId}`, { withCredentials: true });
      setServices(services.filter((s) => s.service_id !== serviceId));
      setStats({ ...stats, total_services: stats.total_services - 1 });
      toast.success("Service deleted");
    } catch (error) {
      toast.error("Failed to delete service");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F7]">
        <Header user={user} />
        <div className="pt-24 flex items-center justify-center h-[60vh]">
          <div className="w-12 h-12 border-4 border-[#1A4D2E] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="admin-dashboard">
      <Header user={user} />

      <main className="pt-24 lg:pt-28 pb-16 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-2 text-[#4A4A4A] hover:text-[#1A4D2E] mb-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Dashboard
              </Link>
              <h1 className="text-3xl font-bold text-[#1A1A1A]">Admin Panel</h1>
            </div>
            <Button
              onClick={fetchData}
              variant="outline"
              className="border-[#1A4D2E] text-[#1A4D2E]"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {/* Stats Grid */}
          {stats && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div className="bg-white rounded-xl border border-stone-200 p-6">
                <Users className="w-8 h-8 text-[#1A4D2E] mb-3" />
                <p className="text-3xl font-bold text-[#1A1A1A]">{stats.total_users}</p>
                <p className="text-[#71717A]">Total Users</p>
              </div>
              <div className="bg-white rounded-xl border border-stone-200 p-6">
                <Building2 className="w-8 h-8 text-[#E07A5F] mb-3" />
                <p className="text-3xl font-bold text-[#1A1A1A]">{stats.total_services}</p>
                <p className="text-[#71717A]">Total Services</p>
              </div>
              <div className="bg-white rounded-xl border border-stone-200 p-6">
                <ShieldCheck className="w-8 h-8 text-amber-500 mb-3" />
                <p className="text-3xl font-bold text-[#1A1A1A]">{stats.pending_services}</p>
                <p className="text-[#71717A]">Pending Approval</p>
              </div>
              <div className="bg-white rounded-xl border border-stone-200 p-6">
                <Star className="w-8 h-8 text-[#4A4A4A] mb-3" />
                <p className="text-3xl font-bold text-[#1A1A1A]">{stats.total_reviews}</p>
                <p className="text-[#71717A]">Total Reviews</p>
              </div>
            </div>
          )}

          {/* Tabs */}
          <Tabs defaultValue="services" className="w-full">
            <TabsList className="mb-6 bg-white border border-stone-200 p-1 rounded-xl">
              <TabsTrigger
                value="services"
                className="rounded-lg data-[state=active]:bg-[#1A4D2E] data-[state=active]:text-white"
                data-testid="services-tab"
              >
                <Building2 className="w-4 h-4 mr-2" />
                Services ({services.length})
              </TabsTrigger>
              <TabsTrigger
                value="users"
                className="rounded-lg data-[state=active]:bg-[#1A4D2E] data-[state=active]:text-white"
                data-testid="users-tab"
              >
                <Users className="w-4 h-4 mr-2" />
                Users ({users.length})
              </TabsTrigger>
            </TabsList>

            {/* Services Tab */}
            <TabsContent value="services">
              <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-stone-50 border-b border-stone-200">
                      <tr>
                        <th className="text-left p-4 font-semibold text-[#1A1A1A]">Service</th>
                        <th className="text-left p-4 font-semibold text-[#1A1A1A]">Category</th>
                        <th className="text-left p-4 font-semibold text-[#1A1A1A]">Status</th>
                        <th className="text-left p-4 font-semibold text-[#1A1A1A]">Rating</th>
                        <th className="text-left p-4 font-semibold text-[#1A1A1A]">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {services.map((service) => (
                        <tr
                          key={service.service_id}
                          className="border-b border-stone-100 last:border-0"
                          data-testid={`service-row-${service.service_id}`}
                        >
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 rounded-lg bg-stone-100 overflow-hidden">
                                {service.image_url ? (
                                  <img
                                    src={service.image_url}
                                    alt={service.name}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <Building2 className="w-6 h-6 text-stone-400" />
                                  </div>
                                )}
                              </div>
                              <div>
                                <p className="font-medium text-[#1A1A1A]">{service.name}</p>
                                <p className="text-sm text-[#71717A]">{service.city}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-4">
                            <span className="text-[#4A4A4A]">{service.category_id}</span>
                          </td>
                          <td className="p-4">
                            <div className="flex flex-col gap-1">
                              {service.is_approved ? (
                                <Badge className="bg-green-100 text-green-700 border-0 w-fit">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Approved
                                </Badge>
                              ) : (
                                <Badge className="bg-amber-100 text-amber-700 border-0 w-fit">
                                  <XCircle className="w-3 h-3 mr-1" />
                                  Pending
                                </Badge>
                              )}
                              {service.is_featured && (
                                <Badge className="bg-[#E07A5F]/10 text-[#E07A5F] border-0 w-fit">
                                  <Crown className="w-3 h-3 mr-1" />
                                  Featured
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="p-4">
                            <StarRating rating={Math.round(service.average_rating || 0)} size="sm" />
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              {!service.is_approved && (
                                <Button
                                  size="sm"
                                  onClick={() => handleApproveService(service.service_id)}
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                  data-testid={`approve-btn-${service.service_id}`}
                                >
                                  Approve
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant={service.is_featured ? "outline" : "default"}
                                onClick={() => handleFeatureService(service.service_id, !service.is_featured)}
                                className={service.is_featured ? "border-[#E07A5F] text-[#E07A5F]" : "bg-[#E07A5F] text-white"}
                                data-testid={`feature-btn-${service.service_id}`}
                              >
                                {service.is_featured ? "Unfeature" : "Feature"}
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Service?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This will permanently delete "{service.name}" and all associated reviews.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDeleteService(service.service_id)}
                                      className="bg-red-600 hover:bg-red-700"
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {services.length === 0 && (
                  <div className="text-center py-12">
                    <Building2 className="w-12 h-12 text-stone-200 mx-auto mb-4" />
                    <p className="text-[#71717A]">No services yet</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Users Tab */}
            <TabsContent value="users">
              <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-stone-50 border-b border-stone-200">
                      <tr>
                        <th className="text-left p-4 font-semibold text-[#1A1A1A]">User</th>
                        <th className="text-left p-4 font-semibold text-[#1A1A1A]">Email</th>
                        <th className="text-left p-4 font-semibold text-[#1A1A1A]">Role</th>
                        <th className="text-left p-4 font-semibold text-[#1A1A1A]">Joined</th>
                        <th className="text-left p-4 font-semibold text-[#1A1A1A]">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr
                          key={u.user_id}
                          className="border-b border-stone-100 last:border-0"
                          data-testid={`user-row-${u.user_id}`}
                        >
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-stone-100 overflow-hidden">
                                {u.picture ? (
                                  <img
                                    src={u.picture}
                                    alt={u.name}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center bg-[#1A4D2E]">
                                    <span className="text-white font-medium">
                                      {u.name?.charAt(0) || "U"}
                                    </span>
                                  </div>
                                )}
                              </div>
                              <span className="font-medium text-[#1A1A1A]">{u.name}</span>
                            </div>
                          </td>
                          <td className="p-4 text-[#4A4A4A]">{u.email}</td>
                          <td className="p-4">
                            <Badge
                              className={`border-0 ${
                                u.role === "admin"
                                  ? "bg-purple-100 text-purple-700"
                                  : u.role === "provider"
                                  ? "bg-blue-100 text-blue-700"
                                  : "bg-stone-100 text-stone-700"
                              }`}
                            >
                              {u.role}
                            </Badge>
                          </td>
                          <td className="p-4 text-[#71717A]">
                            {new Date(u.created_at).toLocaleDateString()}
                          </td>
                          <td className="p-4">
                            {u.user_id !== user.user_id && (
                              <Select
                                value={u.role}
                                onValueChange={(value) => handleUpdateUserRole(u.user_id, value)}
                              >
                                <SelectTrigger className="w-32" data-testid={`role-select-${u.user_id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="customer">Customer</SelectItem>
                                  <SelectItem value="provider">Provider</SelectItem>
                                  <SelectItem value="admin">Admin</SelectItem>
                                </SelectContent>
                              </Select>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {users.length === 0 && (
                  <div className="text-center py-12">
                    <Users className="w-12 h-12 text-stone-200 mx-auto mb-4" />
                    <p className="text-[#71717A]">No users yet</p>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AdminDashboard;
